import React from 'react';
import { Row, TableInstance, TableOptions, useTable } from 'react-table';
import cn from 'classnames';
import styles from './Table.module.css';

type HandleClickFunction<T extends {}> = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: Row<T>) => void;
export type RenderRowFunction<T extends {}> = (row: Row<T>, onClick?: HandleClickFunction<T>) => React.ReactNode;

type TableProps<T extends {}> = Pick<TableOptions<T>, 'data' | 'columns'> & {
    className?: string;
    renderHeader?: (table: TableInstance<T>) => React.ReactNode;
    renderRow?: RenderRowFunction<T>;
    renderBody?: (table: TableInstance<T>, renderRow: RenderRowFunction<T>) => React.ReactNode;
    renderFooter?: (table: TableInstance<T>) => React.ReactNode;
    onRowClick?: HandleClickFunction<T>;
};

const renderDefaultHeader: TableProps<any>['renderHeader'] = ({ headers }) => (
    <thead>
        <tr>
            {headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
            ))}
        </tr>
    </thead>
);

const renderDefaultRow: TableProps<any>['renderRow'] = (row, onClick) => (
    <tr
        {...row.getRowProps()}
        onClick={(e) => {
            if (typeof onClick === 'function') {
                onClick(e, row);
            }
        }}
    >
        {row.cells.map((cell) => (
            <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
        ))}
    </tr>
);

const renderDefaultBody: TableProps<any>['renderBody'] = ({ rows, prepareRow }, renderRow) => (
    <tbody>
        {rows.map((row) => {
            prepareRow(row);
            return renderRow(row);
        })}
    </tbody>
);

const Table = <T extends {}>({
    className,
    onRowClick,
    renderHeader = renderDefaultHeader,
    renderBody = renderDefaultBody,
    renderRow = renderDefaultRow,
    renderFooter,
    ...options
}: TableProps<T>) => {
    const tableInstance = useTable(options);

    return (
        <table className={cn(styles.table, className)}>
            {renderHeader(tableInstance)}
            {renderBody(tableInstance, renderRow)}
            {typeof renderFooter === 'function' && renderFooter(tableInstance)}
        </table>
    );
};

export default Table;
